import { uploadInternalRequestFile } from "@synthesis/shared-services";
import { useTransition } from "@synthesis/shared-services/internal/application/hooks";
import { Button, Modal, ScrollArea, setPromiseSnack, TextArea } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";
import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Uploader } from "../../../components/uploader";
import { useUserInfo } from "../../../shared/hooks/use-user-info";
import { usePageStore } from "../application-detail-page.lib";
import type { TransitionCommentPopupAction } from "./application-control-popup.lib";
import { TRANSITION_SNACK_MESSAGES } from "./application-control-popup.lib";
import { invalidateInternalApplicationQueries } from "../../../shared/utils/invalidate-application-queries";

type TransitionCommentFormValues = {
  comment: string;
};

const COMMENT_REQUIRED_ACTIONS = new Set<TransitionCommentPopupAction["action"]>(["request-revision", "ntp-rework", "ng-rework"]);

const TransitionCommentModal = observer(({ popupAction }: { popupAction: TransitionCommentPopupAction }) => {
  const store = usePageStore();
  const queryClient = useQueryClient();
  const userInfo = useUserInfo();
  const transitionMutation = useTransition();
  const [resultFile, setResultFile] = useState<File | null>(null);
  const isNgApprove = popupAction.action === "ng-approve";
  const isCommentRequired = COMMENT_REQUIRED_ACTIONS.has(popupAction.action);

  const methods = useForm<TransitionCommentFormValues>({
    defaultValues: { comment: "" },
    mode: "onChange",
  });

  const comment = methods.watch("comment");
  const isSubmitDisabled = transitionMutation.isPending || (isCommentRequired && !comment.trim()) || (isNgApprove && !resultFile);

  useEffect(() => {
    methods.reset({ comment: "" });
    setResultFile(null);
  }, [methods, popupAction]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      store?.setPopupAction(null);
    }
  };

  const handleSubmit = methods.handleSubmit(async (formData) => {
    const snackMessages = TRANSITION_SNACK_MESSAGES[popupAction.action];

    const promise = (async () => {
      let resultFileId: string | undefined;

      if (isNgApprove) {
        if (!resultFile || !userInfo) {
          throw new Error("Не удалось загрузить файл ТУ");
        }

        const fileMeta = await uploadInternalRequestFile(
          {
            filename: resultFile.name,
            sourceId: popupAction.requestId,
            sourceType: "INTERNAL_REQUEST",
            userId: userInfo.id,
          },
          resultFile,
        );

        resultFileId = fileMeta.id;

        if (!resultFileId) {
          throw new Error("API не вернул идентификатор файла");
        }
      }

      const trimmedComment = formData.comment.trim();

      await transitionMutation.mutateAsync({
        path: { requestId: popupAction.requestId, action: popupAction.action },
        body: {
          ...(trimmedComment ? { comment: trimmedComment } : {}),
          ...(resultFileId ? { resultFileId } : {}),
        },
      });

      await invalidateInternalApplicationQueries(queryClient, popupAction.requestId);
      store?.setPopupAction(null);
    })();

    setPromiseSnack({
      promise,
      promiseSettings: {
        title: snackMessages,
      },
    });
  });

  return (
    <Modal.Root isOpen setIsOpen={handleOpenChange}>
      <Modal.Content className="flex max-h-[calc(100vh-4rem)] w-[min(700px,calc(100vw-2rem))] flex-col overflow-hidden">
        <Modal.Title>{popupAction.title}</Modal.Title>
        <ScrollArea className="min-h-0 flex-1 overflow-x-hidden [&>[role='presentation']]:!min-w-full">
          <form onSubmit={handleSubmit} className="flex flex-col gap-6 p-8">
            <p className="paragraph-base text-type-minor">{popupAction.description}</p>

            {isNgApprove && (
              <div>
                <h3 className="heading-h4 after:text-system-error-50 mb-4 after:ml-2 after:content-['*']">Файл ТУ</h3>
                <Uploader
                  multiple={false}
                  allowDeleteFromList
                  onSelectFiles={(files) => setResultFile(files[0] ?? null)}
                  onDeleteFromList={() => setResultFile(null)}
                />
              </div>
            )}

            <FormProvider {...methods}>
              <div>
                <h3 className={cn("heading-h4 mb-4", isCommentRequired && "after:text-system-error-50 after:ml-2 after:content-['*']")}>
                  Комментарий
                </h3>
                <TextArea name="comment" rows={4} placeholder={popupAction.commentPlaceholder} disabled={transitionMutation.isPending} />
              </div>
            </FormProvider>

            <div className="flex items-center gap-4">
              <Button type="submit" theme="primary" size="lg" disabled={isSubmitDisabled}>
                {popupAction.submitLabel}
              </Button>
              <Button type="button" theme="secondary" size="lg" disabled={transitionMutation.isPending} onClick={() => handleOpenChange(false)}>
                Отмена
              </Button>
            </div>
          </form>
        </ScrollArea>
      </Modal.Content>
    </Modal.Root>
  );
});

export const ApplicationControlModals = observer(() => {
  const store = usePageStore();
  const popupAction = store?.popupAction;

  if (!popupAction || popupAction.type !== "transition-comment") {
    return null;
  }

  return <TransitionCommentModal popupAction={popupAction} />;
});
